import type {
    ProductVariant,
} from '@b2b-platform/commerce/types/product';

import type { LineItem } from '~/packages/commerce/types/cart';

type TrackingProduct = {
    product: string;
    product_id: string;
    quantity: number;
    value: number;
};

const mapProductVariant = (productVariant: ProductVariant, quantity: number): TrackingProduct => ({
    product: productVariant.fullName,
    product_id: productVariant.id,
    quantity: quantity,
    value: Number(productVariant?.priceTiers[0]?.price?.amount) / 100,
});

const mapLineItem = (lineItem: LineItem): TrackingProduct => ({
    product: lineItem.fullName,
    product_id: lineItem.productVariantId,
    quantity: lineItem.quantity,
    value: Number(lineItem.price.amount) / 100,
});

export const useCommerceTracking = () => {
    useGtm();

    const { user } = useUser();

    const trackAddToCart = (
        productVariant: ProductVariant,
        quantity: number,
        totalPrice: number,
        addonId: string | null = null,
    ) => {
        const products: TrackingProduct[] = [];

        products.push(mapProductVariant(productVariant, quantity));

        if (productVariant.includedAddons.data.length > 0) {
            const includedAddon = productVariant.includedAddons.data[0];
            products.push(mapProductVariant(includedAddon, quantity));
        }

        if (addonId) {
            const addon = productVariant.availableAddons.data.find(
                (availableAddon) => availableAddon.id === addonId,
            );
            if (addon) products.push(mapProductVariant(addon, quantity));
        }

        window?.dataLayer?.push({
            event: 'add-to-cart',
            products,
            products_total_value: totalPrice / 100,
            user_data: {
                email: user.value?.email ?? null
            }
        });
    };

    const trackBeginCheckout = (
        lineItems: any,
        totalPrice: number,
    ) => {
        window?.dataLayer?.push({
            products: null
        });

        window?.dataLayer?.push({
            event: "begin_checkout",
            products: getProductForDataLayerTracking(lineItems),
            products_total_value: totalPrice / 100,
            user_data: {
                email: user.value?.email ?? null
            }
        })
    }

    const trackContactRequest = (name: string = '', method: string = '') => {
        window?.dataLayer?.push({
            event: 'product_request_success',
            method: method,
            products: name,
        });
    };

    const getProductForDataLayerTracking = (
        lineItems: LineItem[]
    ): TrackingProduct[] => {
        const products: TrackingProduct[] = [];

        lineItems.forEach(item => {
            products.push(mapLineItem(item));
            item.addons.data.forEach(addon => {
                products.push(mapLineItem(addon));
            });
        });

        return products;
    };

    return {
        trackAddToCart,
        trackBeginCheckout,
        trackContactRequest,
    };
};
